// src/app/service/Module.service.ts
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams
} from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UzorService } from '../encryption/uzor.service';

/* ---------------------------------------------------------------------
   Types – exactly the same shape the backend returns
   --------------------------------------------------------------------- */
export interface ServiceRouteMap {
  get?: string | string[];
  post?: string | string[];
  put?: string | string[];
  patch?: string | string[];
  delete?: string | string[];
  [method: string]: string | string[] | undefined;
}
export interface ServiceApiMap {
  [serviceKey: string]: ServiceRouteMap;
}
export interface ModuleInfo {
  name: string;
  ip: string | null;
  port: number | null;
  type: string | null;
  serverUrl: string | null;
  apiEndpoint: string | null;
  initialized: boolean;
  built: boolean;
  hasBackendEnv?: boolean;
  hasFrontendEnv?: boolean;
  isLoadable: boolean;
  dailyHash?: string | null;
  dailyHashShort?: string | null;
  dateGenerated?: string | null;
  clientPath?: string | null;
  serverPath?: string | null;
  publicPath?: string | null;
  note?: string;
  publicservices?: string[];
  api?: ServiceApiMap;
}
interface ModuleListResponse {
  success: boolean;
  modules?: ModuleInfo[];
  module?: ModuleInfo;
  error?: string;
  receivedRequestAt: string;
  processingTimeMs: number;
  data: any;
  _cookies?: Record<string, any>;
}
export interface ModuleCallResponse<T = any> {
  success: boolean;
  error?: string;
  receivedRequestAt: string;
  processingTimeMs: number;
  data: T | null;
  information?: Record<string, { publicservices: string[]; api: ServiceApiMap }>;
  _cookies?: Record<string, any>;
}
export type HttpVerb = 'get' | 'post' | 'put' | 'patch' | 'delete';

@Injectable({
  providedIn: 'root'
})
export class ModuleService {
  /* -----------------------------------------------------------------
     Config & caching
     ----------------------------------------------------------------- */
  private readonly gatewayBase = (environment.firewall || '').replace(/\/+$/, '');
  private readonly moduleUrl = `${this.gatewayBase}/api/agpt/module`;
  private readonly cacheTtlMs = 60_000; // 60 s

  private modulesSubject = new BehaviorSubject<ModuleInfo[]>([]);
  public modules$: Observable<ModuleInfo[]> = this.modulesSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  private errorSubject = new BehaviorSubject<string | null>(null);
  public error$ = this.errorSubject.asObservable();

  private initializedSubject = new BehaviorSubject<boolean>(false);
  public initialized$ = this.initializedSubject.asObservable();

  private lastFetchedAt = 0;
  private inFlightFetch: Observable<ModuleInfo[]> | null = null;

  constructor(
    private http: HttpClient,
    private uzor: UzorService                // → provides hashSha256()
  ) {}

  /* -----------------------------------------------------------------
     1️⃣  Public bootstrap helpers – init / refresh / ensureLoaded
     ----------------------------------------------------------------- */
  init(force = false): Observable<ModuleInfo[]> {
    return this.fetchModules(force).pipe(
      tap(() => this.initializedSubject.next(true))
    );
  }

  /** Call when you just need the list but want the cache‑TTL to be honoured. */
  ensureLoaded(): Observable<ModuleInfo[]> {
    const stale = Date.now() - this.lastFetchedAt > this.cacheTtlMs;
    if (this.initializedSubject.value && !stale) {
      return of(this.modulesSubject.value);
    }
    return this.fetchModules(false);
  }

  /** Force a fresh fetch, ignoring the TTL. */
  refresh(): Observable<ModuleInfo[]> {
    return this.fetchModules(true);
  }

  /* -----------------------------------------------------------------
     2️⃣  Internal GET used by the three public bootstrap methods.
          – **Important** – every request now carries the custom
            `App` / `Ak` headers so the back‑end can validate it.
     ----------------------------------------------------------------- */
  private fetchModules(force: boolean): Observable<ModuleInfo[]> {
    const stale = Date.now() - this.lastFetchedAt > this.cacheTtlMs;

    // Return cached data if it is still fresh
    if (!force && !stale && this.initializedSubject.value) {
      return of(this.modulesSubject.value);
    }
    if (this.inFlightFetch) {
      return this.inFlightFetch;
    }

    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    /** -------------------------------------------------------------
        Build the security headers that **must** be present on *every*
        request to the gateway (including the module‑list call).
        ------------------------------------------------------------- */
    const appName = (environment.appName || '').trim(); // e.g. “Business”
    const dailyKey = this._dailyKeyFor(appName);
    let secHeaders = new HttpHeaders();

    if (dailyKey) {
      // HttpHeaders are immutable – each `set` returns a new instance.
      secHeaders = secHeaders.set('App', appName).set('Ak', dailyKey);
    }

    /* -------------------------------------------------------------
       Perform the GET, attach the security headers and store the
       result in the cache.
       ------------------------------------------------------------- */
    const request$ = this.http
      .get<ModuleListResponse>(this.moduleUrl, { headers: secHeaders })
      .pipe(
        map(res => {
          if (!res.success) {
            throw new Error(res.error || 'Failed to fetch modules');
          }
          return res.modules ?? [];
        }),
        tap(mods => {
          this.modulesSubject.next(mods);
          this.lastFetchedAt = Date.now();
        }),
        catchError(err => {
          const msg = this.extractErrorMessage(err);
          this.errorSubject.next(msg);
          // keep the previous cache on error
          return of(this.modulesSubject.value);
        }),
        finalize(() => {
          this.loadingSubject.next(false);
          this.inFlightFetch = null;
        })
      );

    this.inFlightFetch = request$;
    return request$;
  }

  /* -----------------------------------------------------------------
     3️⃣  Read‑only helpers (snapshot / lookup)
     ----------------------------------------------------------------- */
  getModulesSnapshot(): ModuleInfo[] {
    return this.modulesSubject.value;
  }

  getModule(appName: string): Observable<ModuleInfo | undefined> {
    return this.modules$.pipe(
      map(list => list.find(m => m.name.toLowerCase() === appName.toLowerCase()))
    );
  }

  getModuleSnapshot(appName: string): ModuleInfo | undefined {
    return this.modulesSubject.value.find(
      m => m.name.toLowerCase() === appName.toLowerCase()
    );
  }

  getPublicServices(appName: string): Observable<string[]> {
    return this.getModule(appName).pipe(map(m => m?.publicservices || []));
  }

  getServiceApiMap(appName: string): Observable<ServiceApiMap> {
    return this.getModule(appName).pipe(map(m => m?.api || {}));
  }

  getServiceRoutes(
    appName: string,
    serviceName: string
  ): Observable<ServiceRouteMap | undefined> {
    return this.getServiceApiMap(appName).pipe(map(api => api[serviceName]));
  }

  /* -----------------------------------------------------------------
     4️⃣  DAILY‑KEY generation (client side) – **exactly** the same
         algorithm the back‑end uses (see AppService.key()).
     ----------------------------------------------------------------- */
  /**
   * Build the key:  SHA256( `${appName}${serverUrl}${YYYY}${MM}${DD}` )
   * Returns `null` when the app cannot be found in the cached module‑list
   * or when the cached entry has no `serverUrl`.
   */
  private _dailyKeyFor(appName: string): string | null {
    // const mod = this.getModuleSnapshot(appName);
    // if (!mod?.serverUrl) {
    //   // No info yet – the client cannot compute the key.
    //   return null;
    // }
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    const pattern = `${appName}https://${environment.serverAddress}:${environment.port}${y}${m}${d}`;
    return this.uzor.hashSha256(pattern);
  }

  /* -----------------------------------------------------------------
     5️⃣  CORE caller – always adds the custom security headers
     ----------------------------------------------------------------- */
  call<T = any>(
    appName: string,
    serviceName: string,
    target: string,
    method: HttpVerb = 'get',
    body: any = null,
    params?: Record<string, string | number | boolean>
  ): Observable<ModuleCallResponse<T>> {
    /* ---- our mandatory app‑identification headers ------------- */
    const dailyKey = this._dailyKeyFor(appName);
    /* ---- internal proxy headers required by the gateway ---- */
    let headers = new HttpHeaders({
      _app: appName,
      _service: serviceName,
      _target: target
    });
    console.log(dailyKey, 'dailyKey');

    if (dailyKey) {
      headers = headers.set('App', appName).set('Ak', dailyKey);
    }

    /* ---- query‑string handling for GET / DELETE -------------- */
    let httpParams = new HttpParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        httpParams = httpParams.set(k, String(v));
      });
    }

    const options = {
      headers,
      params: httpParams,
      observe: 'body' as const
    };

    /* ---- dispatch according to the HTTP verb ---------------- */
    switch (method) {
      case 'get':
        return this.http
          .get<ModuleCallResponse<T>>(this.moduleUrl, options)
          .pipe(catchError(err => this.normalizeCallError<T>(err)));
      case 'delete':
        return this.http
          .delete<ModuleCallResponse<T>>(this.moduleUrl, options)
          .pipe(catchError(err => this.normalizeCallError<T>(err)));
      case 'post':
        return this.http
          .post<ModuleCallResponse<T>>(this.moduleUrl, body, options)
          .pipe(catchError(err => this.normalizeCallError<T>(err)));
      case 'put':
        return this.http
          .put<ModuleCallResponse<T>>(this.moduleUrl, body, options)
          .pipe(catchError(err => this.normalizeCallError<T>(err)));
      case 'patch':
        return this.http
          .patch<ModuleCallResponse<T>>(this.moduleUrl, body, options)
          .pipe(catchError(err => this.normalizeCallError<T>(err)));
      default:
        return throwError(() => new Error(`Unsupported HTTP verb: ${method}`));
    }
  }

  /* -----------------------------------------------------------------
     Convenience wrappers – all funnel through `call()`
     ----------------------------------------------------------------- */
  get<T = any>(appName: string, serviceName: string, target: string, params?: Record<string, string | number | boolean>) {
    return this.call<T>(appName, serviceName, target, 'get', null, params);
  }
  post<T = any>(appName: string, serviceName: string, target: string, body: any) {
    return this.call<T>(appName, serviceName, target, 'post', body);
  }
  put<T = any>(appName: string, serviceName: string, target: string, body: any) {
    return this.call<T>(appName, serviceName, target, 'put', body);
  }
  patch<T = any>(appName: string, serviceName: string, target: string, body: any) {
    return this.call<T>(appName, serviceName, target, 'patch', body);
  }
  delete<T = any>(appName: string, serviceName: string, target: string, params?: Record<string, string | number | boolean>) {
    return this.call<T>(appName, serviceName, target, 'delete', null, params);
  }

  /* -----------------------------------------------------------------
     INTERNAL – normalise errors so callers always receive the
                 shared envelope shape.
     ----------------------------------------------------------------- */
  private normalizeCallError<T>(err: HttpErrorResponse | any): Observable<ModuleCallResponse<T>> {
    const body = err?.error;
    if (body && typeof body === 'object' && 'success' in body) {
      return of(body as ModuleCallResponse<T>);
    }
    return of({
      success: false,
      error: this.extractErrorMessage(err),
      receivedRequestAt: new Date().toISOString(),
      processingTimeMs: 0,
      data: null
    });
  }

  private extractErrorMessage(err: HttpErrorResponse | Error | any): string {
    if (err instanceof HttpErrorResponse) {
      return err.error?.error || err.message || `HTTP ${err.status}`;
    }
    if (err instanceof Error) {
      return err.message;
    }
    return 'Unknown error';
  }
}
